import React, { useState, useEffect } from "react"
import Header from "../../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Paper, Typography, Grid, Card, Stack, Snackbar, Tooltip, Button } from "@mui/material"
import { Home, CompassCalibration, North, South, East, West, ArrowUpward, ArrowDownward } from "@mui/icons-material"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import CustomBackdrop from "../../../components/CustomBackdrop"
import Swal from "sweetalert2"
import CustomAutocomplete from "../../../components/CustomAutocomplete"
import CustomTextFieldReadable from "../../../components/CustomTextFieldReadable"
import CustomTextField from "../../../components/CustomTextField"
import { useQuery, useMutation, api } from "../../../api"
import MapaUbicacion from "../Components/MapaUbicacion"
import EditarIcon from "../../../assets/iconos/EjecutarAccion.ico"

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const ESTADOS = [
  { value: "ACTIVO", label: "ACTIVO" },
  { value: "INACTIVO", label: "INACTIVO" },
]

const EditarCabeceraUnicaPredio = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { preid } = location.state
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [errors, setErrors] = useState({})

  // Obtener tipos de predios
  const {
    data: { data: tiposPrediosCB } = [],
    isLoading: isLoadingtiposPrediosCB,
    isRefetching: isRefetchtiposPrediosCB,
  } = useQuery({
    queryKey: ["tiposPrediosCBEditarCabeceraUnicaPredio"],
    url: "/ActivacionDePredios/getAllTiposPrediosCB",
  })

  // Obtener datos específicos del predio
  const {
    data: { data: predioData } = {},
    isLoading: isLoadingPredio,
    isRefetching: isRefetchingPredio,
  } = useQuery({
    queryKey: ["getSpecificCabeceraPredio", preid],
    url: "/ActivacionDePredios/getSpecificCabeceraPredio",
    params: { preid },
    enabled: !!preid, // Solo ejecutar si hay preid
    staleTime: 0,
  })

  // Mutación para editar
  const { mutate: updatePredio, isPending: isUpdatingPredio } = useMutation({
    fn: async (data) => {
      const response = await api.post("/ActivacionDePredios/updateCabeceraUnicaPredio", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "silent",
    onSuccess: (data) => {
      Swal.fire({
        icon: "success",
        title: "¡Actualizado con éxito!",
        text: "El predio ha sido actualizado correctamente.",
        confirmButtonText: "OK",
      }).then(() => {
        navigate(-1)
      })
    },
  })

  // Estado inicial del formulario
  const [formData, setFormData] = useState({
    preid: "",
    preidmat: "",
    precatastro: "",
    tippreid: null,
    preunidad: "",
    preubicacion: "",
    presuperficie: "",
    prelindnorte: "",
    preobslindnorte: "",
    prelindsur: "",
    preobslindsur: "",
    prelindeste: "",
    preobslindeste: "",
    prelindoeste: "",
    preobslindoeste: "",
    prelindoarriba: "",
    prelindoabajo: "",
    prealicuota: "0.00",
    precuerpodif: "",
    preestado: null,
  })
  const [coordenadas, setCoordenadas] = useState({
    lat: "",
    lng: "",
    precision: "",
  })

  // Cargar datos cuando se recibe la respuesta
  useEffect(() => {
    if (predioData) {
      const predio = predioData

      // Formatear coordenadas si existen
      if (predio.precordenadas) {
        const [lat, lng, precision] = predio.precordenadas.split(";")
        setCoordenadas({
          lat: lat || "",
          lng: lng || "",
          precision: precision || "",
        })
      }

      // Formatear tippreid como objeto para el autocomplete
      let tippreidValue = null
      if (predio.tippreid && tiposPrediosCB) {
        tippreidValue = tiposPrediosCB.find((t) => t.value === predio.tippreid) || null
      }

      // Formatear preestado como objeto para el autocomplete
      let preestadoValue = null
      if (predio.preestado && ESTADOS) {
        preestadoValue = ESTADOS.find((t) => t.value === predio.preestado) || null
      }

      setFormData({
        preid: predio.preid || "",
        preidmat: predio.preidmat || "",
        precatastro: predio.precatastro || "",
        tippreid: tippreidValue,
        preunidad: predio.preunidad || "",
        preubicacion: predio.preubicacion || "",
        presuperficie: predio.presuperficie || "",
        prelindnorte: predio.prelindnorte || "",
        preobslindnorte: predio.preobslindnorte || "",
        prelindsur: predio.prelindsur || "",
        preobslindsur: predio.preobslindsur || "",
        prelindeste: predio.prelindeste || "",
        preobslindeste: predio.preobslindeste || "",
        prelindoeste: predio.prelindoeste || "",
        preobslindoeste: predio.preobslindoeste || "",
        prelindoarriba: predio.prelindoarriba || "",
        prelindoabajo: predio.prelindoabajo || "",
        prealicuota: predio.prealicuota?.toString() || "0.00",
        precuerpodif: predio.precuerpodif || "",
        preestado: preestadoValue,
      })
    }
  }, [predioData, tiposPrediosCB])

  // Validar formulario
  const validateForm = () => {
    const newErrors = {}

    if (!formData.preid.trim()) {
      newErrors.preid = "La matrícula es requerida"
    }

    if (!formData.precatastro.trim()) {
      newErrors.precatastro = "El código catastral es requerido"
    }

    if (!formData.tippreid) {
      newErrors.tippreid = "El tipo de predio es requerido"
    }

    if (!formData.preubicacion.trim()) {
      newErrors.preubicacion = "La ubicación es requerida"
    }

    if (!formData.presuperficie.trim()) {
      newErrors.presuperficie = "La superficie es requerida"
    }

    // Validar alícuota
    const alicuota = parseFloat(formData.prealicuota)
    if (isNaN(alicuota) || alicuota < 0) {
      newErrors.prealicuota = "La alícuota debe ser un número mayor o igual a 0"
    }

    // Validar la ubicacion
    if (!coordenadas.lat || !coordenadas.lng) {
      newErrors.ubicacion = "La ubicación en el mapa es requerida"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Manejar cambios en los campos
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Limpiar error del campo
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }))
    }
  }

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      setSnackbarMessage("Por favor, complete todos los campos requeridos correctamente")
      setSnackbarOpen(true)
      return
    }

    // Confirmar actualización
    const result = await Swal.fire({
      title: "Confirmar actualización",
      text: "¿Está seguro de actualizar este predio?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, actualizar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#196C87",
      allowOutsideClick: false,
      allowEscapeKey: false,
    })

    if (result.isConfirmed) {
      const dataToSave = {
        ...formData,
        preid: formData.preid, // Asegurar que se envía el ID
        prealicuota: parseFloat(formData.prealicuota) || 0,
        precordenadas: `${coordenadas.lat};${coordenadas.lng};${coordenadas.precision}`,
      }
      updatePredio(dataToSave)
    }
  }

  // Calcular loading state
  const isLoading =
    isLoadingtiposPrediosCB || isRefetchtiposPrediosCB || isLoadingPredio || isRefetchingPredio || isUpdatingPredio

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Predio</b>
        </div>
        <div>
          <Tooltip title="Actualizar">
            <Button color="primary" onClick={handleSubmit} sx={{ marginBottom: "15px" }}>
              <img src={EditarIcon} alt="Editar" style={{ width: "30px" }} />
            </Button>
          </Tooltip>
        </div>

        <CustomBackdrop isLoading={isLoading} />

        <Box sx={StyledRoot}>
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3, background: "white" }} component="form">
            {/* Información General del Predio */}
            <Typography
              variant="h5"
              gutterBottom
              sx={{ color: "primary.main", mb: 3, display: "flex", alignItems: "center", gap: 1 }}
            >
              <Home /> Información General del Predio
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <CustomAutocomplete
                  label="Estado *"
                  selectedOption={formData.preestado}
                  setSelectedOption={(tipo) => {
                    handleInputChange("preestado", tipo)
                  }}
                  options={ESTADOS}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomTextField
                  fullWidth
                  label="Matrícula *"
                  value={formData.preid}
                  onChange={(e) => handleInputChange("preid", e.target.value)}
                  error={!!errors.preid}
                  helperText={errors.preid}
                  inputProps={{ maxLength: 18 }}
                  disabled // La matrícula no debería editarse
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextField
                  fullWidth
                  label="ID Matriz *"
                  value={formData.preidmat}
                  onChange={(e) => handleInputChange("preidmat", e.target.value)}
                  error={!!errors.preidmat}
                  helperText={errors.preidmat}
                  inputProps={{ maxLength: 18 }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextField
                  fullWidth
                  label="Código Catastral *"
                  value={formData.precatastro}
                  onChange={(e) => handleInputChange("precatastro", e.target.value)}
                  error={!!errors.precatastro}
                  helperText={errors.precatastro}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomAutocomplete
                  label="Tipo de Predio *"
                  selectedOption={formData.tippreid}
                  setSelectedOption={(tipo) => {
                    handleInputChange("tippreid", tipo)
                  }}
                  options={tiposPrediosCB || []}
                />
                {errors.tippreid && (
                  <Typography variant="caption" color="error">
                    {errors.tippreid}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextField
                  fullWidth
                  label="Unidad *"
                  value={formData.preunidad}
                  onChange={(e) => handleInputChange("preunidad", e.target.value)}
                  error={!!errors.preunidad}
                  helperText={errors.preunidad}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextField
                  fullWidth
                  label="Ubicación *"
                  value={formData.preubicacion}
                  onChange={(e) => handleInputChange("preubicacion", e.target.value)}
                  error={!!errors.preubicacion}
                  helperText={errors.preubicacion}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomTextField
                  fullWidth
                  label="Superficie *"
                  value={formData.presuperficie}
                  onChange={(e) => handleInputChange("presuperficie", e.target.value)}
                  error={!!errors.presuperficie}
                  helperText={errors.presuperficie}
                />
              </Grid>

              {/* Tercera fila: Alícuota, Estado, Cuerpo Diferente */}
              <Grid item xs={12} md={3}>
                <CustomTextField
                  fullWidth
                  label="Alícuota *"
                  type="number"
                  value={formData.prealicuota}
                  onChange={(e) => handleInputChange("prealicuota", e.target.value)}
                  error={!!errors.prealicuota}
                  inputProps={{ step: "0.01", min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomTextField
                  fullWidth
                  label="Cuerpo Diferente"
                  value={formData.precuerpodif}
                  onChange={(e) => handleInputChange("precuerpodif", e.target.value)}
                />
              </Grid>

              {/* Sección de Linderos */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ mt: 2, mb: 2, color: "primary.main", display: "flex", alignItems: "center", gap: 1 }}
                >
                  <CompassCalibration /> Linderos del Predio
                </Typography>
              </Grid>

              {/* Norte */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <North color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Norte
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    <CustomTextField
                      fullWidth
                      label="Descripción del Lindero Norte"
                      value={formData.prelindnorte}
                      onChange={(e) => handleInputChange("prelindnorte", e.target.value)}
                      multiline
                      rows={2}
                    />
                    <CustomTextField
                      fullWidth
                      label="Observación Técnica Norte"
                      value={formData.preobslindnorte}
                      onChange={(e) => handleInputChange("preobslindnorte", e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Card>
              </Grid>

              {/* Sur */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <South color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Sur
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    <CustomTextField
                      fullWidth
                      label="Descripción del Lindero Sur"
                      value={formData.prelindsur}
                      onChange={(e) => handleInputChange("prelindsur", e.target.value)}
                      multiline
                      rows={2}
                    />
                    <CustomTextField
                      fullWidth
                      label="Observación Técnica Sur"
                      value={formData.preobslindsur}
                      onChange={(e) => handleInputChange("preobslindsur", e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Card>
              </Grid>

              {/* Este */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <East color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Este
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    <CustomTextField
                      fullWidth
                      label="Descripción del Lindero Este"
                      value={formData.prelindeste}
                      onChange={(e) => handleInputChange("prelindeste", e.target.value)}
                      multiline
                      rows={2}
                    />
                    <CustomTextField
                      fullWidth
                      label="Observación Técnica Este"
                      value={formData.preobslindeste}
                      onChange={(e) => handleInputChange("preobslindeste", e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Card>
              </Grid>

              {/* Oeste */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <West color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Oeste
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    <CustomTextField
                      fullWidth
                      label="Descripción del Lindero Oeste"
                      value={formData.prelindoeste}
                      onChange={(e) => handleInputChange("prelindoeste", e.target.value)}
                      multiline
                      rows={2}
                    />
                    <CustomTextField
                      fullWidth
                      label="Observación Técnica Oeste"
                      value={formData.preobslindoeste}
                      onChange={(e) => handleInputChange("preobslindoeste", e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Card>
              </Grid>

              {/* Arriba y Abajo */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <ArrowUpward color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Arriba
                    </Typography>
                  </Box>
                  <CustomTextField
                    fullWidth
                    label="Descripción del Lindero Arriba"
                    value={formData.prelindoarriba}
                    onChange={(e) => handleInputChange("prelindoarriba", e.target.value)}
                    multiline
                    rows={2}
                  />
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <ArrowDownward color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Lindero Abajo
                    </Typography>
                  </Box>
                  <CustomTextField
                    fullWidth
                    label="Descripción del Lindero Abajo"
                    value={formData.prelindoabajo}
                    onChange={(e) => handleInputChange("prelindoabajo", e.target.value)}
                    multiline
                    rows={2}
                  />
                </Card>
              </Grid>

              <Grid item xs={12}>
                <MapaUbicacion
                  label={"Ubicación del Predio"}
                  value={coordenadas}
                  onChange={(nuevaUbicacion) => {
                    setCoordenadas({
                      lat: nuevaUbicacion.lat,
                      lng: nuevaUbicacion.lng,
                      precision: nuevaUbicacion.precision,
                    })
                  }}
                  isMobile={false}
                  modo="manual"
                />
                {errors.ubicacion && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.ubicacion}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </div>
    </ThemeProvider>
  )
}

export default EditarCabeceraUnicaPredio
